import axios, { AxiosError, AxiosResponse } from "axios";
import i18next from "i18next";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

import { LoadResponse } from "../../models/api/load/load.response";
import { Page } from "../../models/api/page";
import { ListSortDirection, Sort } from "../../models/api/sort";
import { DocumentLinkDTO, DocumentLinkSTO, DocumentLinkDataDTO, DocumentLinkFilter } from "../../models/document-link";
import { DownloadedDocumentFile } from "../../models/documents/document-files";

import { SearchOptions } from "../../models/search-options/search-options";

import { toastifyResponse } from "../../utils/toastify-response/toastify-response";
import { BaseApiService } from "../base/base-api.service";

export class DocumentLinkApiService extends BaseApiService<DocumentLinkDTO, DocumentLinkSTO> {
  constructor() {
    super({
      servicePath: "/DocumentLinks",
      loadItemsPath: "/GetDocumentLinksInfo",
      saveItemsPath: "/SaveDocumentLinks",
    });
    this.defaultSort = [
      {
        name: "createdAt",
        direction: ListSortDirection.Ascending,
      } as Sort,
    ];
  }

  /**
   * Downloads Document link files contents in a zip archive
   */
  public async loadDocumentLinkFiles(
    documentLinkId: string,
    email: string,
    last4DigitsOfPhoneNumber: string,
    signal?: AbortSignal
  ): Promise<DownloadedDocumentFile> {
    const request = this.createLoadRequest(
      {
        documentLinkId,
        email,
        last4DigitsOfPhoneNumber,
      } as DocumentLinkFilter,
      "",
      this.defaultSort,
      { pageNumber: 0, pageSize: 1000 },
      false,
      60
    );

    let response: AxiosResponse<Blob, any> = {
      data: new Blob(),
      status: 200,
      statusText: "OK",
      headers: {},
      config: {},
    };
    let errorMessage = "";

    const url = `${import.meta.env.VITE_API_URL}/DocumentLinks/GetDocumentLinksFilesContents`;

    try {
      response = await axios.post<Blob>(url, request, {
        responseType: "blob",
        signal,
      });
    } catch (err) {
      const error = err as AxiosError;
      errorMessage = error?.response?.data?.error ? error?.response?.data?.error : error?.message ?? error;

      console.log("Failure loading document files. ", errorMessage);

      switch (error?.response?.status) {
        case 400: {
          errorMessage = "Bad request/wrong data sent.";
          break;
        }
        case 401: {
          errorMessage = "Not authenticated. Try to sign in again.";
          break;
        }
        case 403: {
          errorMessage = "Not authorized. Review your access rights.";
          break;
        }
        case 404: {
          errorMessage = "Data not found.";
          break;
        }

        case 500: {
          errorMessage = "Failure loading document files - Server error.";
          break;
        }

        case 502: {
          errorMessage = "Bad gateway / endpoint unreachable.";
          break;
        }

        case 503: {
          errorMessage = "Service unavailable.";
          break;
        }

        default:
          break;
      }
    }

    const { data, headers } = response;
    const contentDispositionHeader = headers["content-disposition"] ?? "";
    const fileName = this.getFileName(contentDispositionHeader);

    return { name: fileName, data, error: errorMessage };
  }

  private getFileName(contentDispositionHeader: string): string {
    const fileNameUtf8Attr = "filename*=UTF-8''";
    const fileNameAttr = "filename=";
    let fileName = "";
    if (contentDispositionHeader.indexOf(fileNameUtf8Attr) !== -1) {
      fileName = contentDispositionHeader.split(fileNameUtf8Attr)[1].split(";")[0];
    } else if (contentDispositionHeader.indexOf(fileNameAttr) !== -1) {
      fileName = contentDispositionHeader.split(fileNameAttr)[1].split(";")[0];
    }

    return fileName;
  }

  public async loadBlob(
    documentLinkId: string,
    email: string,
    last4DigitsOfPhoneNumber: string,
    fileId: string
  ): Promise<DownloadedDocumentFile> {
    const request = this.createLoadRequest(
      {
        documentLinkId,
        email,
        last4DigitsOfPhoneNumber,
        fileId,
        isDownloaded: true,
      } as DocumentLinkFilter,
      "",
      this.defaultSort,
      { pageNumber: 0, pageSize: 1 },
      false,
      60
    );

    const url = `${this.apiURL}${this.apiPaths.servicePath}/GetDocumentLinkFileContents`;

    let response: AxiosResponse<Blob, any> = {
      data: new Blob(),
      status: 200,
      statusText: "OK",
      headers: {},
      config: {},
    };
    let errorMessage = "";
    try {
      response = await axios.post<Blob>(url, request, {
        responseType: "blob",
      });
    } catch (err) {
      const error = err as AxiosError;
      errorMessage = error?.response?.data?.error ? error?.response?.data?.error : error?.message ?? error;

      console.log(`${error?.response?.status ?? ""}: Failure loading file. `, errorMessage);

      switch (error?.response?.status) {
        case 400: {
          errorMessage = "Bad request/wrong data sent.";
          break;
        }
        case 401: {
          errorMessage = "Not authenticated. Try to sign in again.";
          break;
        }
        case 403: {
          errorMessage = "Not authorized. Review your access rights.";
          break;
        }
        case 404: {
          errorMessage = "Data not found.";
          break;
        }

        case 500: {
          errorMessage = "Failure loading file - Server error.";
          break;
        }

        case 502: {
          errorMessage = "Bad gateway / endpoint unreachable.";
          break;
        }

        case 503: {
          errorMessage = "Service unavailable.";
          break;
        }

        default:
          break;
      }
    }

    const { data, headers } = response;
    const contentDispositionHeader = headers["content-disposition"] ?? "";
    const fileName = this.getFileName(contentDispositionHeader);

    return { name: fileName, data, error: errorMessage };
  }
}

export const loadDocumentLinkInfo = async (documentLinkId: string) => {
  const response = await new DocumentLinkApiService().loadItems(
    {
      documentLinkId,
    } as DocumentLinkFilter,
    undefined,
    [{ name: "createdAt", direction: ListSortDirection.Ascending }] as Sort[],
    { pageNumber: 0, pageSize: 1000 },
    undefined,
    60
  );

  toastifyResponse(response, "failed");

  return { response };
};

export const loadDocumentLinkData = async (documentLinkId: string, email: string, last4DigitsOfPhoneNumber: string) => {
  const request = {
    id: uuidv4(),
    invokerId: uuidv4(),
    correlationId: uuidv4(),
    timeout: 60,
    currentCompanyAccount: null,
    searchFilter: "",
    propsFilter: {
      documentLinkId,
      email,
      last4DigitsOfPhoneNumber,
    } as DocumentLinkFilter,
    sorts: [{ name: "createdAt", direction: ListSortDirection.Ascending }] as Sort[],
    undefined,
  };

  let response = {
    data: {
      items: [] as DocumentLinkDataDTO[],
      totalItems: 0,
    } as LoadResponse<DocumentLinkDataDTO>,
  };

  const url = `${import.meta.env.VITE_API_URL}/DocumentLinks/GetDocumentLinksData`;
  try {
    response = await axios.post<LoadResponse<DocumentLinkDataDTO>>(url, request);
  } catch (err) {
    const error = err as AxiosError;
    let errorMessage = error?.response?.data?.error ?? error?.message ?? error;

    console.log(`${error?.response?.status ?? ""}: Failure loading data. `, errorMessage);

    switch (error?.response?.status) {
      case 400: {
        errorMessage = "Bad request/wrong data sent.";
        break;
      }
      case 401: {
        errorMessage = "Not authenticated. Try to sign in again.";
        break;
      }
      case 403: {
        errorMessage = "Not authorized. Review your access rights.";
        break;
      }
      case 404: {
        errorMessage = "Data not found.";
        break;
      }

      case 500: {
        errorMessage = "Server error.";
        break;
      }

      case 502: {
        errorMessage = "Bad gateway / endpoint unreachable.";
        break;
      }

      case 503: {
        errorMessage = "Service unavailable.";
        break;
      }

      default:
        break;
    }

    response.data.error = errorMessage;
  }

  return response.data as LoadResponse<DocumentLinkDataDTO>;
};

export const generateDocumentLink = async (documentLink: DocumentLinkSTO) => {
  const response = await new DocumentLinkApiService().saveItems([documentLink]);
  toastifyResponse(response);
  return response;
};

export const loadLegalEntityUsers = async (
  signal: AbortSignal | undefined = undefined,
  id: string,
  propsFilter: {
    // legalEntity :  LegalEntityUserFilter;
  },
  page: Page = { pageNumber: 0, pageSize: 1000 },
  searchOptions: SearchOptions
) => {
  const response = await new DocumentLinkApiService().loadItems(
    {
      // legalEntityId : propsFilter.legalEntity.legalEntityId
    },

    searchOptions.query,
    undefined,
    page,
    searchOptions.exactMatch,
    60,
    signal
  );

  toastifyResponse(response, "failed");

  return response;
};

export const downloadBlob = (blob: Blob, name = "file.txt", downloadCallback?: () => void) => {
  try {
    const aFile = new Blob([blob], { type: blob.type });
    // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
    const blobUrl = URL.createObjectURL(aFile);

    // Create a link element
    const link = document.createElement("a");

    // Set link's href to point to the Blob URL
    link.href = blobUrl;
    link.download = name;

    // Append link to the body
    document.body.appendChild(link);

    // Dispatch click event on the link
    // This is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
    downloadCallback && downloadCallback();
    // Remove link from body
    document.body.removeChild(link);
  } catch (error) {
    console.log("Failure saving file", name, error);
  }
};

export const handleDownloadDocsFiles = async (
  documentLinkId: string,
  email: string,
  last4DigitsOfPhoneNumber: string
) => {
  const confirmed = window.confirm(
    `${i18next.t("components.upload_document.download_document_files_confirmation_message")}`
  );

  if (confirmed) {
    const { data, name, error }: DownloadedDocumentFile = await new DocumentLinkApiService().loadDocumentLinkFiles(
      documentLinkId,
      email,
      last4DigitsOfPhoneNumber
    );

    error ? toast(error) : downloadBlob(data, name);
  }
};

export const handleDownloadDocFile = async (
  documentLinkId: string,
  email: string,
  last4DigitsOfPhoneNumber: string,
  fileId: string,
  downloadCallback?: () => void
) => {
  const confirmed = window.confirm(
    `${i18next.t("components.upload_document.document_files_table.download_confirmation_message")}`
  );

  if (confirmed) {
    const { data, name, error }: DownloadedDocumentFile = await new DocumentLinkApiService().loadBlob(
      documentLinkId,
      email,
      last4DigitsOfPhoneNumber,
      fileId
    );

    error ? toast(error) : downloadBlob(data, name, downloadCallback);
  }
};
